// Browse-page state: filters, sort, density, visible columns, saved views per domain.

import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface BrowseView {
  id: string;
  name: string;
  filters: Record<string, string>;
  sort?: string;
  visibleColumns: string[];
}

interface BrowseState {
  byDomain: Record<string, {
    q: string;
    filters: Record<string, string>;
    sort?: string;
    page: number;
    pageSize: number;
    visibleColumns: string[];
    views: BrowseView[];
    activeViewId?: string;
  }>;
  setQuery: (domain: string, q: string) => void;
  setFilter: (domain: string, key: string, value: string) => void;
  clearFilters: (domain: string) => void;
  setSort: (domain: string, sort: string) => void;
  setPage: (domain: string, page: number) => void;
  setColumns: (domain: string, cols: string[]) => void;
  saveView: (domain: string, name: string) => void;
  applyView: (domain: string, id: string) => void;
  deleteView: (domain: string, id: string) => void;
}

const defaults = (): BrowseState["byDomain"][string] => ({
  q: "", filters: {}, page: 0, pageSize: 50, visibleColumns: [], views: [],
});

export const useBrowseStore = create<BrowseState>()(
  persist(
    (set) => ({
      byDomain: {},
      setQuery: (d, q) => set(s => ({ byDomain: { ...s.byDomain, [d]: { ...(s.byDomain[d] ?? defaults()), q, page: 0 } } })),
      setFilter: (d, key, value) => set(s => {
        const cur = s.byDomain[d] ?? defaults();
        const filters = { ...cur.filters };
        if (value === "" || value == null) delete filters[key]; else filters[key] = value;
        return { byDomain: { ...s.byDomain, [d]: { ...cur, filters, page: 0 } } };
      }),
      clearFilters: (d) => set(s => ({ byDomain: { ...s.byDomain, [d]: { ...(s.byDomain[d] ?? defaults()), filters: {}, q: "", page: 0 } } })),
      setSort: (d, sort) => set(s => ({ byDomain: { ...s.byDomain, [d]: { ...(s.byDomain[d] ?? defaults()), sort } } })),
      setPage: (d, page) => set(s => ({ byDomain: { ...s.byDomain, [d]: { ...(s.byDomain[d] ?? defaults()), page } } })),
      setColumns: (d, visibleColumns) => set(s => ({ byDomain: { ...s.byDomain, [d]: { ...(s.byDomain[d] ?? defaults()), visibleColumns } } })),
      saveView: (d, name) => set(s => {
        const cur = s.byDomain[d] ?? defaults();
        const view: BrowseView = { id: Math.random().toString(36).slice(2, 8), name, filters: cur.filters, sort: cur.sort, visibleColumns: cur.visibleColumns };
        return { byDomain: { ...s.byDomain, [d]: { ...cur, views: [...cur.views, view], activeViewId: view.id } } };
      }),
      applyView: (d, id) => set(s => {
        const cur = s.byDomain[d] ?? defaults();
        const v = cur.views.find(x => x.id === id);
        if (!v) return s;
        return { byDomain: { ...s.byDomain, [d]: { ...cur, filters: v.filters, sort: v.sort, visibleColumns: v.visibleColumns, activeViewId: id } } };
      }),
      deleteView: (d, id) => set(s => {
        const cur = s.byDomain[d] ?? defaults();
        return { byDomain: { ...s.byDomain, [d]: { ...cur, views: cur.views.filter(v => v.id !== id), activeViewId: cur.activeViewId === id ? undefined : cur.activeViewId } } };
      }),
      // For convenience to consumers
      ...(undefined as unknown as object),
    }),
    { name: "lexicon.browse" },
  ),
);

export function getBrowse(domain: string) {
  const s = useBrowseStore.getState().byDomain[domain] ?? defaults();
  return s;
}
